import * as React from 'react'
import { Link, useSearchParams } from 'react-router'
import { Loader2 } from 'lucide-react'
import {
  OAUTH_SCOPE_LABELS,
  OAUTH_SCOPES_START_UNCHECKED,
  type OauthScope,
} from '@lib/constants/oauth-scopes'
import type { OauthConsentRequestDto } from '@lib/dto/oauth-consent.dto'
import { oauthConsentDecisionInputSchema } from '@lib/input/oauth-consent.input'
import { parseLinkToken } from '@lib/validators/link-token.validator'
import { useAuth } from '@/auth/AuthContext'
import {
  useDecideOauthConsent,
  useOauthConsentRequest,
} from '@/hooks/useOauth'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'

const SCOPE_CHECKBOX_CLASS =
  'mt-0.5 h-4 w-4 shrink-0 rounded border-default accent-brand'

/**
 * Leave for the address the API returned.
 *
 * A full page load, not the client router: the code exists only on that URL.
 * A method, rather than a bare call, so the redirect can be observed without
 * the browser actually leaving.
 */
export const consentNavigation = {
  follow(redirectTo: string): void {
    window.location.assign(redirectTo)
  },
}

/**
 * The screen where a signed-in person lets an assistant work as them.
 *
 * Outside the app shell on purpose: this is a decision about an app, not a
 * page of the product, and the nav would bury the only fact that matters —
 * where the code is about to be sent.
 *
 * The request id arrives as `?request=`. It is kept across the sign-in
 * redirect. Without one, or with one cut short or otherwise not the shape the
 * API issues, there is nothing to ask, and this page does not call the API.
 */
export function OauthConsentPage() {
  const [params] = useSearchParams()
  const requestId = requestIdFromSearch(params.get('request'))
  const request = useOauthConsentRequest(requestId)

  if (requestId === null) return <IncompleteConnection />
  if (request.isPending) return <ConsentLoading />
  if (request.isError) return <ConsentRequestFailed error={request.error} />
  if (request.data === undefined) return <ConsentRequestFailed error={null} />
  return <ConsentForm requestId={requestId} request={request.data} />
}

function requestIdFromSearch(value: string | null): string | null {
  return parseLinkToken(value?.trim())
}

/**
 * Read stays on while anything else is allowed. The checkbox is disabled for
 * that stretch, so it cannot be cleared out from under the other choices.
 */
function readStaysOn(
  requested: readonly OauthScope[],
  checked: readonly OauthScope[],
): boolean {
  if (!requested.includes('records:read')) return false
  return checked.some((scope) => scope !== 'records:read')
}

function selectedScopes(
  requested: readonly OauthScope[],
  checked: readonly OauthScope[],
): OauthScope[] {
  const selected = new Set(checked)
  if (readStaysOn(requested, checked)) selected.add('records:read')
  return requested.filter((scope) => selected.has(scope))
}

function initialChecked(requested: readonly OauthScope[]): OauthScope[] {
  const starting = requested.filter(
    (scope) => !OAUTH_SCOPES_START_UNCHECKED.includes(scope),
  )
  return selectedScopes(requested, starting)
}

function toggleScope(
  requested: readonly OauthScope[],
  checked: readonly OauthScope[],
  scope: OauthScope,
  on: boolean,
): OauthScope[] {
  const next = on
    ? [...checked, scope]
    : checked.filter((item) => item !== scope)
  return selectedScopes(requested, next)
}

function uniqueScopes(scopes: readonly OauthScope[]): OauthScope[] {
  const seen = new Set<OauthScope>()
  const result: OauthScope[] = []
  for (const scope of scopes) {
    if (seen.has(scope)) continue
    seen.add(scope)
    result.push(scope)
  }
  return result
}

function scopeInputId(scope: OauthScope): string {
  return `oauth-scope-${scope.replaceAll(':', '-')}`
}

interface ConsentLayoutProps {
  children: React.ReactNode
}

function ConsentLayout({ children }: ConsentLayoutProps) {
  return (
    <div className="min-h-svh bg-app px-3 py-4 sm:px-4 sm:py-6">
      <div className="mx-auto max-w-2xl space-y-6">{children}</div>
    </div>
  )
}

function BackToSmithwell() {
  return (
    <Button variant="outline" asChild>
      <Link to="/">Back to Smithwell</Link>
    </Button>
  )
}

function IncompleteConnection() {
  return (
    <ConsentLayout>
      <h1 className="text-xl font-semibold">Connect an app</h1>
      <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
        <p className="text-sm text-secondary-text">This link is incomplete.</p>
        <BackToSmithwell />
      </section>
    </ConsentLayout>
  )
}

function ConsentLoading() {
  return (
    <ConsentLayout>
      <h1 className="text-xl font-semibold">Connect an app</h1>
      <section className="rounded-lg border border-default bg-surface p-4">
        <p className="flex items-center gap-2 text-sm text-muted-text">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading the connection…
        </p>
      </section>
    </ConsentLayout>
  )
}

interface ExpiredConnectionProps {
  message: string
}

function ExpiredConnection({ message }: ExpiredConnectionProps) {
  return (
    <ConsentLayout>
      <h1 className="text-xl font-semibold">This connection link has ended</h1>
      <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
        <p className="text-sm text-secondary-text">{message}</p>
        <BackToSmithwell />
      </section>
    </ConsentLayout>
  )
}

interface ConsentRequestFailedProps {
  error: Error | null
}

function ConsentRequestFailed({ error }: ConsentRequestFailedProps) {
  if (isApiError(error) && error.status === 410) {
    return (
      <ExpiredConnection
        message={
          error.body.message ??
          'This connection link has expired or was already used.'
        }
      />
    )
  }
  if (isApiError(error) && error.status === 404) {
    return (
      <ConsentNotice
        title="Connect an app"
        message={
          error.body.message ?? 'Smithwell does not have that connection link.'
        }
      />
    )
  }
  return (
    <ConsentNotice
      title="Could not open the connection"
      message={requestFailureMessage(error)}
    />
  )
}

function requestFailureMessage(error: Error | null): string {
  if (error !== null && isApiError(error)) {
    return (
      error.body.message ?? 'Something went wrong opening that connection.'
    )
  }
  if (error === null) return 'Something went wrong opening that connection.'
  return toErrorMessage(error)
}

interface ConsentNoticeProps {
  title: string
  message: string
}

function ConsentNotice({ title, message }: ConsentNoticeProps) {
  return (
    <ConsentLayout>
      <h1 className="text-xl font-semibold">{title}</h1>
      <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
        <p className="text-sm text-secondary-text">{message}</p>
        <BackToSmithwell />
      </section>
    </ConsentLayout>
  )
}

interface ConsentFormProps {
  requestId: string
  request: OauthConsentRequestDto
}

type DecisionKind = 'approve' | 'deny'

function ConsentForm({ requestId, request }: ConsentFormProps) {
  const { user } = useAuth()
  const decide = useDecideOauthConsent()
  const requested = uniqueScopes(request.requestedScopes)
  const [checked, setChecked] = React.useState<OauthScope[]>(() =>
    initialChecked(requested),
  )
  const [pending, setPending] = React.useState<DecisionKind | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)
  const [expiredMessage, setExpiredMessage] = React.useState<string | null>(
    null,
  )

  const clientName = request.clientName.trim()
  const who = clientName === '' ? 'this app' : clientName
  const title = clientName === '' ? 'Connect an app' : `Connect ${clientName}`

  async function submitDecision(approve: boolean): Promise<void> {
    const parsed = oauthConsentDecisionInputSchema.safeParse({
      approve,
      scopes: selectedScopes(requested, checked),
    })
    if (!parsed.success) {
      setFormError('Smithwell could not send that answer.')
      return
    }

    setFormError(null)
    setPending(approve ? 'approve' : 'deny')
    try {
      const result = await decide.mutateAsync({
        requestId,
        input: parsed.data,
      })
      consentNavigation.follow(result.redirectTo)
    } catch (caught) {
      if (isApiError(caught) && caught.status === 410) {
        setExpiredMessage(
          caught.body.message ??
            'This connection link has expired or was already used.',
        )
        return
      }
      setFormError(
        isApiError(caught)
          ? (caught.body.message ?? 'Could not finish connecting.')
          : 'Could not finish connecting.',
      )
      setPending(null)
    }
  }

  if (expiredMessage !== null) {
    return <ExpiredConnection message={expiredMessage} />
  }

  return (
    <ConsentLayout>
      <header>
        <h1 className="break-words text-xl font-semibold">{title}</h1>
        {clientName === '' ? null : (
          <p className="mt-1 text-sm text-muted-text">
            {clientName} is the name this app gave itself.
          </p>
        )}
      </header>

      <section className="space-y-4 rounded-lg border border-default bg-surface p-4">
        <p className="text-sm">
          You are letting {who} work in Smithwell as you.
        </p>
        <p className="break-words text-sm font-semibold text-primary-text">
          A code will be sent to {request.redirectHost}.
        </p>
        {request.loopbackOnly ? (
          <p className="text-sm text-secondary-text">
            This code comes back to an app on this computer, not to a website.
          </p>
        ) : null}

        <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[8rem_1fr]">
          <dt className="text-muted-text">Signed in</dt>
          <dd className="min-w-0">
            <span className="block truncate">{user?.name ?? '—'}</span>
            <span className="block truncate text-muted-text">
              {user?.email ?? '—'}
            </span>
          </dd>
        </dl>

        <p className="text-sm text-secondary-text">
          Your customers cannot connect an assistant. Only people with a
          Smithwell login can.
        </p>
        <p className="text-sm text-secondary-text">
          Your password stays on the sign-in page. {who} does not receive it.
        </p>

        <fieldset className="space-y-3">
          <legend className="sr-only">What this app asked to do</legend>
          {requested.map((scope) => (
            <ScopeChoice
              key={scope}
              scope={scope}
              checked={checked.includes(scope)}
              disabled={
                pending !== null ||
                (scope === 'records:read' && readStaysOn(requested, checked))
              }
              onChange={(on) => {
                setChecked(toggleScope(requested, checked, scope, on))
              }}
            />
          ))}
        </fieldset>

        {formError !== null ? (
          <p className="text-sm text-danger">{formError}</p>
        ) : null}

        <div className="flex items-center gap-2">
          <Button
            type="button"
            disabled={pending !== null}
            onClick={() => {
              void submitDecision(true)
            }}
          >
            {pending === 'approve' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            Approve
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={pending !== null}
            onClick={() => {
              void submitDecision(false)
            }}
          >
            {pending === 'deny' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            Cancel
          </Button>
        </div>
      </section>
    </ConsentLayout>
  )
}

interface ScopeChoiceProps {
  scope: OauthScope
  checked: boolean
  disabled: boolean
  onChange: (on: boolean) => void
}

function ScopeChoice({
  scope,
  checked,
  disabled,
  onChange,
}: ScopeChoiceProps) {
  const id = scopeInputId(scope)
  return (
    <div className="flex gap-2.5">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        className={SCOPE_CHECKBOX_CLASS}
        onChange={(event) => {
          onChange(event.target.checked)
        }}
      />
      <Label
        htmlFor={id}
        className="font-normal leading-snug text-primary-text"
      >
        {OAUTH_SCOPE_LABELS[scope]}
      </Label>
    </div>
  )
}
